/**
 * HandoverDialog — START a patient handover: the composer over the platform's
 * collaborative-session blocks. The handover SESSION is the Rapport. It does
 * not move custody: who holds the patient is the allocation write
 * (PUT /patients/{id}/assignment, the allocation board).
 *
 * Confirming composes the flow:
 *   1. create a handover-agent session bound to the patient, seeded with the
 *      outgoing nurse's note + flagged conversations (prose — the message IS
 *      the record; the agent grounds its draft Rapport on it)
 *   2. share it editor with the incoming nurse (multi-writer)
 *   3. hand the session to the caller (select it / navigate to it)
 *
 * The curation (which conversations the colleague should see first) and the
 * note are the nurse's customisation of the Rapport; they land IN the session
 * transcript, not in any side-channel payload.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Check } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ShiftNursePicker, type OnShiftNurse, type SelectedNurse } from '@/components/clinical/ShiftNursePicker';
import { SessionIdentityRow } from '@/components/session/SessionIdentityRow';
import { useStatePill } from '@/components/session/ConversationCard';
import { useSessions, useCreateSession, useShareSession } from '@/hooks/useSessions';
import { useWardAllocation } from '@/hooks/useShifts';
import { localId } from '@/lib/reference';
import { useCurrentUser } from '@/hooks/useAuth';
import { sessionDisplayTitle } from '@/lib/sessionDisplay';
import { errorToast } from '@/hooks/errorToast';
import { randomUUID } from '@/lib/uuid';
import { cn } from '@/lib/utils';
import { HANDOVER_AGENT_SLUG } from '@/lib/sessionRouting';
import type { SessionResponse } from '@/api/types_session';

interface HandoverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientLabel: string;
  /** Nurses to suppress in the picker (at least the outgoing nurse). */
  excludeNurseIds: string[];
  /** Receives the new handover session — select it (Home) or navigate (Shifts). */
  onStarted: (session: SessionResponse) => void;
}

export default function HandoverDialog({
  open,
  onOpenChange,
  patientId,
  patientLabel,
  excludeNurseIds,
  onStarted,
}: HandoverDialogProps) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const { data: sessions } = useSessions();
  const createSession = useCreateSession();
  const shareSession = useShareSession();

  // The caller's ward — the same frame the allocation board reads. Its on-shift
  // nurses ARE the valid handover targets (the allocation write requires the
  // incoming nurse to be on an open shift), so the picker browses them instead of asking
  // the nurse to type a name blind. No ward on the profile → the query is
  // disabled and the picker degrades to its search-only fallback.
  const wardId = localId(me?.locationRef, 'Location') ?? undefined;
  const ward = useWardAllocation(wardId);
  const onShiftNurses = useMemo<OnShiftNurse[]>(() => {
    const exclude = new Set(excludeNurseIds);
    return (ward.data?.on_shift ?? [])
      .filter((n) => !exclude.has(n.nurse_id))
      .map((n) => ({ id: n.nurse_id, name: n.name, patientCount: n.patient_count }));
  }, [ward.data, excludeNurseIds]);

  const [incomingNurse, setIncomingNurse] = useState<SelectedNurse | null>(null);
  const [note, setNote] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isStarting, setIsStarting] = useState(false);
  // ONE create key per dialog-open: a retry after a mid-flow failure replays
  // the SAME create (the server returns the cached session), so the flow
  // converges on one session instead of minting a second.
  const flowKeys = useRef<{ create: string } | null>(null);

  // This patient's conversations, newest-first (API order preserved).
  const patientSessions = useMemo(
    () => (sessions ?? []).filter((s) => s.subject?.type === 'patient' && s.subject.id === patientId),
    [sessions, patientId],
  );

  // Seed the curation each time the dialog opens: the live working set
  // (active / needs_review) travels by default; resolved work is opt-in.
  useEffect(() => {
    if (!open) return;
    setIncomingNurse(null);
    setNote('');
    setIsStarting(false);
    flowKeys.current = null;
    setSelectedIds(
      new Set(
        patientSessions
          .filter((s) => s.status !== 'archived')
          .map((s) => s.id),
      ),
    );
    // Seed on open only — a background refetch must not wipe the nurse's picks
    // (patientSessions is deliberately NOT a dependency).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patientId]);

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Compose the seed message: the note in the nurse's words plus the flagged
  // conversations as prose — the transcript IS the record, no side-channel.
  const composeSeed = (): string => {
    const flagged = patientSessions.filter((s) => selectedIds.has(s.id));
    const parts: string[] = [];
    if (note.trim()) parts.push(note.trim());
    if (flagged.length > 0) {
      parts.push(
        t('shifts.handover.flaggedIntro', {
          defaultValue: 'Flagged for the handover:',
        }) +
          '\n' +
          flagged.map((s) => `- ${sessionDisplayTitle(s, t)}`).join('\n'),
      );
    }
    if (parts.length === 0) {
      parts.push(
        t('shifts.handover.defaultSeed', {
          defaultValue: 'I am handing over this patient. Please draft a short Rapport.',
        }),
      );
    }
    return parts.join('\n\n');
  };

  const handleStart = async () => {
    if (!incomingNurse || !me || isStarting) return;
    setIsStarting(true);
    if (!flowKeys.current) {
      flowKeys.current = { create: randomUUID() };
    }
    const keys = flowKeys.current;
    try {
      // 1. The handover session, bound to the patient and SEEDED (creating
      //    runs the session: the seed is its first message — the agent's
      //    grounded draft starts on the same call; replayed on retry).
      const session = await createSession.mutateAsync({
        agentSlug: HANDOVER_AGENT_SLUG,
        message: composeSeed(),
        subject: { type: 'patient', id: patientId },
        title: t('shifts.handover.sessionTitle', {
          patient: patientLabel,
          defaultValue: 'Handover {{patient}}',
        }),
        idempotencyKey: keys.create,
      });
      // 2. Multi-writer: the incoming nurse joins as editor.
      await shareSession.mutateAsync({
        sessionId: session.id,
        principalType: 'user',
        principalId: incomingNurse.id,
        role: 'editor',
      });
      onOpenChange(false);
      onStarted(session);
    } catch {
      // The underlying hooks/api surfaced their toasts; add the flow-level one.
      errorToast({
        context: 'startHandover',
        description: t('shifts.handover.startError', {
          defaultValue: 'The handover could not be started.',
        }),
      });
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" aria-hidden="true" />
            {t('shifts.handover.title', { defaultValue: 'Hand over patient' })}
          </SheetTitle>
          <SheetDescription>{patientLabel}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex-1 space-y-6 overflow-y-auto pb-4">
          <ShiftNursePicker
            inputId="handover-incoming-nurse"
            value={incomingNurse}
            onChange={setIncomingNurse}
            onShiftNurses={onShiftNurses}
            isLoading={ward.isLoading}
            isError={ward.isError}
            onRetry={() => void ward.refetch()}
            excludeIds={excludeNurseIds}
          />

          {patientSessions.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
                {t('shifts.handover.curation', {
                  defaultValue: 'What travels with the patient',
                })}
              </h3>
              <p className="text-[13px] text-neutral-500">
                {t('shifts.handover.curationHint', {
                  defaultValue:
                    'Flag the conversations your colleague should see first.',
                })}
              </p>
              <ul className="space-y-1.5">
                {patientSessions.map((s) => (
                  <HandoverSessionRow
                    key={s.id}
                    session={s}
                    checked={selectedIds.has(s.id)}
                    onToggle={() => toggle(s.id)}
                  />
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-1.5">
            <label
              htmlFor="handover-note"
              className="block text-[15px] font-medium text-neutral-900"
            >
              {t('shifts.handover.noteLabel', { defaultValue: 'Handover note' })}
            </label>
            <textarea
              id="handover-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={t('shifts.handover.notePlaceholder', {
                defaultValue: 'Anything your colleague should know…',
              })}
              className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
            />
          </div>
        </div>

        <div className="shrink-0 border-t border-neutral-200 pt-4 pb-safe">
          <Button
            size="pill"
            onClick={() => void handleStart()}
            disabled={!incomingNurse || isStarting}
            className="min-h-[44px] w-full text-[17px]"
          >
            {isStarting
              ? t('shifts.handover.starting', { defaultValue: 'Starting handover…' })
              : t('shifts.handover.start', { defaultValue: 'Start handover' })}
          </Button>
          <p className="mt-2 text-center text-[12px] text-neutral-400">
            {t('shifts.handover.startHint', {
              defaultValue:
                'Your colleague joins the Rapport. Responsibility stays with you until the patient is reassigned.',
            })}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** One toggleable conversation row: check + shared identity block + state pill. */
function HandoverSessionRow({
  session,
  checked,
  onToggle,
}: {
  session: SessionResponse;
  checked: boolean;
  onToggle: () => void;
}) {
  const pill = useStatePill(session.status === 'archived' ? 'done' : 'active');

  return (
    <li>
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={onToggle}
        className={cn(
          'flex w-full min-h-[44px] items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors',
          checked
            ? 'border-primary-100 bg-primary-50/60'
            : 'border-neutral-200 bg-white hover:bg-neutral-50',
        )}
      >
        <span
          className={cn(
            'grid h-5 w-5 flex-none place-items-center rounded-md border',
            checked
              ? 'border-primary bg-primary text-white'
              : 'border-neutral-300 bg-white text-transparent',
          )}
          aria-hidden="true"
        >
          <Check className="h-3.5 w-3.5" />
        </span>
        <SessionIdentityRow session={session} />
        <span
          className={cn(
            'inline-flex flex-none items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
            pill.classes,
          )}
        >
          {pill.icon}
          {pill.label}
        </span>
      </button>
    </li>
  );
}
